import React, { useRef, useState } from 'react';
import { PHONE_DISPLAY, PHONE_HREF, ADDRESS_LINE2 } from '../../config/business';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Heading, Text, Input, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import LandingHeader from '../../components/LandingHeader/LandingHeader';
import LandingFooter from '../../components/LandingFooter/LandingFooter';
import Seo from '../../components/Seo/Seo';
import Turnstile from '../../components/Turnstile';
import { captureLead, LEAD_SEGMENTS } from '../../utils/leadCapture';
import { BIO_PLY_FEATURES } from '../../utils/bioPlyFeatures';
import { heroSrcSet, HERO_SIZES } from '../../utils/responsiveImage.mjs';
import './Landing.css';
import Icon from '../../components/ui/Icon';
import LatestNews from './LatestNews';

const MotionBox = motion(Box);
const publicUrl = (import.meta.env.BASE_URL || '').replace(/\/$/, '');
const heroImageUrl = `${publicUrl}/images/hero-racing.jpg`;
const ctaImageUrl = `${publicUrl}/images/cta-section.png`;
const expertImageUrl = `${publicUrl}/images/lab-team.jpg`;

export default function Landing() {
  const navigate = useNavigate();
  const [contactEmail, setContactEmail] = useState('');
  const [contactError, setContactError] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);

  // Saves the typed email as a contact lead, then hands the visitor off to the
  // enrollment form with that address prefilled — so the lead is kept even if
  // they abandon the longer form.
  //
  // Awaited, and a failure stops here instead of navigating: the capture goes
  // through Turnstile verification in submit-lead, so a rejection now means the
  // enrollment signup would be rejected the same way — better to say so here.
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactEmail.trim()) {
      navigate('/enroll');
      return;
    }
    if (contactSubmitting) return;

    setContactSubmitting(true);
    setContactError('');

    const { error: captureError } = await captureLead({
      email: contactEmail,
      segment: LEAD_SEGMENTS.CONTACT,
      source: 'landing',
      payload: { intent: 'bio_ply_inquiry' },
      token: captchaToken,
    });

    // Token is single-use; clear it whatever the outcome so a retry
    // re-challenges.
    turnstileRef.current?.reset();
    setCaptchaToken('');
    setContactSubmitting(false);

    if (captureError) {
      setContactError('Something went wrong sending your email. Please try again.');
      return;
    }

    // Carry the address in router state rather than the query string so it
    // never lands in a URL, history entry, or referrer.
    navigate('/enroll', { state: { email: contactEmail.trim() } });
  };

  return (
    <div className="landing-page">
      <Seo
        title="Qentoros | Peptide-Powered Biologics for Equine Health"
        description="Qentoros develops BIO-PLY™ and a growing pipeline of platelet-derived biologics for high-impact equine conditions — clearing infection, calming inflammation, and accelerating tissue healing."
        path="/"
      />
      <LandingHeader />

      <main id="main" tabIndex={-1}>
        {/* Hero */}
        <section className="landing-hero" aria-label="Hero">
          <div className="landing-hero-media" aria-hidden="true">
            {/* AVIF/WebP rungs ahead of the original JPEG: at the 960w rung a
                phone pulls ~36 KB instead of ~188 KB, which is the bulk of the
                mobile LCP. The <link rel=preload> injected by
                scripts/prerender-seo.mjs uses the same avif srcset + sizes, so
                the preload and the source picked here are always the same file. */}
            <picture>
              <source type="image/avif" srcSet={heroSrcSet(heroImageUrl, 'avif')} sizes={HERO_SIZES} />
              <source type="image/webp" srcSet={heroSrcSet(heroImageUrl, 'webp')} sizes={HERO_SIZES} />
              <img
                className="landing-hero-image"
                src={heroImageUrl}
                alt=""
                width="1600"
                height="1064"
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          </div>
          <div className="landing-hero-scrim" aria-hidden="true" />

          <div className="landing-hero-content">
            {/* Entrance is a CSS animation (not framer-motion) so it plays on
                first paint from the prerendered HTML — before the JS bundle
                loads — keeping the LCP hero copy visible immediately. */}
            <div className="landing-hero-copy">
              <div className="landing-hero-kicker">
                <div className="landing-hero-kicker-line">Powered by peptides</div>
                <div className="landing-hero-kicker-line">Proven through clinical trials</div>
              </div>

              <h1 className="landing-hero-headline">
                A <span className="landing-hero-accent">new class</span> of therapeutics
              </h1>

              <Text className="landing-hero-subhead" color="whiteAlpha.900" fontSize={{ base: 'md', md: 'lg' }}>
                Advancing BIO-PLY&trade; and a growing pipeline of biologics for high-impact veterinary conditions.
              </Text>

              <div className="landing-hero-cta">
                <Button
                  as={Link}
                  to="/products"
                  size="lg"
                  className="landing-hero-primary-btn"
                  bg="var(--color-primary-maroon)"
                  color="white"
                  fontWeight={700}
                  borderRadius="var(--radius-full)"
                  px={8}
                  _hover={{ bg: 'var(--color-primary-dark)', transform: 'translateY(-1px)' }}
                  _active={{ transform: 'translateY(0px)' }}
                >
                  Explore our products
                </Button>
                <Button
                  as={Link}
                  to="/contact"
                  size="lg"
                  className="landing-hero-secondary-btn"
                  color="white"
                  fontWeight={700}
                  borderRadius="var(--radius-full)"
                  px={8}
                  bg="rgba(255, 255, 255, 0.12)"
                  border="1px solid rgba(255, 255, 255, 0.55)"
                  _hover={{ bg: 'rgba(255, 255, 255, 0.22)', borderColor: 'rgba(255, 255, 255, 0.8)', transform: 'translateY(-1px)' }}
                  _active={{ transform: 'translateY(0px)' }}
                >
                  Contact us
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="landing-section" aria-labelledby="features-heading">
          <Heading as="h2" id="features-heading" size="2xl" textAlign="center" color="gray.900">
            Meet our flagship product, BIO-PLY™
          </Heading>
          <Text textAlign="center" color="gray.600" maxW="640px" mx="auto" mt={3} mb={2}>
            Platelet-derived peptides developed specifically for the treatment of septic arthritis.
          </Text>
          {/* Shared with the sign-in brand panel — edit utils/bioPlyFeatures. */}
          <div className="landing-features-grid">
            {BIO_PLY_FEATURES.map((feature) => (
              <div className="landing-feature-card" key={feature.title}>
                <div className="landing-feature-icon">
                  <Icon name={feature.icon} style={{ fontSize: 28 }} />
                </div>
                <Heading as="h3" size="md" mb={2}>{feature.title}</Heading>
                <Text color="gray.600" fontSize="sm">
                  {feature.body}
                </Text>
              </div>
            ))}
          </div>
          <div className="landing-features-audience">
            <MotionBox
              className="landing-features-audience-card"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-20px' }}
              transition={{ duration: 0.4 }}
            >
              <div className="landing-features-audience-icon" aria-hidden="true">
                <Icon name="medical_services" />
              </div>
              <h3 className="landing-features-audience-title">For veterinarians</h3>
              <p className="landing-features-audience-desc">
                Add <Link to="/products/bio-ply" className="landing-inline-link">BIO-PLY™</Link> to your toolkit for joint sepsis, synovitis, and refractory soft-tissue injuries, over a short course of intra-articular treatment.
              </p>
            </MotionBox>
            <MotionBox
              className="landing-features-audience-card"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-20px' }}
              transition={{ duration: 0.4, delay: 0.08 }}
            >
              <div className="landing-features-audience-icon" aria-hidden="true">
                <Icon name="pets" />
              </div>
              <h3 className="landing-features-audience-title">For horse owners</h3>
              <p className="landing-features-audience-desc">
                Give your horse the best chance at recovery. <Link to="/products/bio-ply" className="landing-inline-link">BIO-PLY™</Link> is used by leading equine referral centers, including in cases that haven't responded to other treatments.
              </p>
            </MotionBox>
          </div>
        </section>

        {/* Flagship product action box — sits directly under the BIO-PLY™ intro
            and boxes above, and is the page's "learn more" route to the product
            page. */}
        <section className="landing-cta-banner" aria-labelledby="cta-banner-heading">
          <div className="landing-cta-banner-bg" aria-hidden="true" />
          <div className="landing-cta-banner-shapes" aria-hidden="true">
            <div className="landing-cta-banner-shape landing-cta-banner-shape-1" />
            <div className="landing-cta-banner-shape landing-cta-banner-shape-2" />
          </div>
          <MotionBox
            className="landing-cta-banner-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
          >
            <div className="landing-cta-banner-card">
              <div className="landing-cta-banner-accent" aria-hidden="true" />
              <div className="landing-cta-banner-content">
                <p className="landing-cta-banner-eyebrow">Flagship product</p>
                <Heading
                  as="h2"
                  id="cta-banner-heading"
                  color="gray.900"
                  mb={3}
                  className="landing-cta-banner-headline"
                >
                  Learn more about <span className="landing-cta-banner-highlight">BIO-PLY™</span>
                </Heading>
                <Text color="gray.600" mb={6} maxW="560px" mx="auto" className="landing-cta-banner-sub">
                  A first-in-class, triple-action peptide biologic developed specifically for equine septic joints —
                  ready to use, straight from the vial.
                </Text>

                <div className="landing-cta-banner-actions">
                  <Button
                    as={Link}
                    to="/products/bio-ply"
                    className="landing-cta-banner-btn"
                    size="lg"
                    bg="transparent"
                    color="white"
                    background="linear-gradient(135deg, var(--color-cta-orange) 0%, var(--color-primary-maroon) 100%)"
                    _hover={{
                      background: 'linear-gradient(135deg, var(--color-cta-orange-hover) 0%, var(--color-primary-dark) 100%)',
                      transform: 'scale(1.02)',
                      boxShadow: '0 8px 24px rgba(139, 21, 56, 0.35)',
                    }}
                    _active={{ transform: 'scale(0.98)' }}
                    transition="transform 0.2s ease, box-shadow 0.2s ease"
                    borderRadius="var(--radius-xl)"
                    px={8}
                    py={6}
                    fontWeight={600}
                    rightIcon={<Icon name="arrow_forward" style={{ fontSize: 18 }} />}
                  >
                    Learn more here
                  </Button>
                  <Button
                    as={Link}
                    to="/contact"
                    className="landing-cta-banner-btn-secondary"
                    size="lg"
                    variant="outline"
                    borderColor="var(--color-gray-300)"
                    color="gray.800"
                    _hover={{
                      bg: 'var(--color-gray-50)',
                      borderColor: 'var(--color-primary-maroon)',
                      color: 'var(--color-primary-maroon)',
                    }}
                    borderRadius="var(--radius-xl)"
                    px={8}
                    py={6}
                    fontWeight={600}
                  >
                    Talk to our team
                  </Button>
                </div>
              </div>
            </div>
          </MotionBox>
        </section>

        {/* Expert / About — modern split with clear hierarchy */}
        <section id="about" className="landing-expert" aria-labelledby="about-heading">
          <div className="landing-expert-visual" aria-hidden="true">
            <div className="landing-expert-visual-bg" />
            <MotionBox
              className="landing-expert-image-wrap"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5 }}
            >
              {/* Decorative: sits in an aria-hidden visual column; the adjacent
                  "Expert-led, horse-first" copy conveys the meaning. */}
              <img src={expertImageUrl} alt="" />
            </MotionBox>
          </div>
          <div className="landing-expert-content">
            <MotionBox
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45 }}
              className="landing-expert-content-inner"
            >
              <Link to="/about/team" className="landing-expert-eyebrow landing-expert-eyebrow-link">Our expertise</Link>
              <div className="landing-expert-accent" />
              <Heading as="h2" id="about-heading" size="xl" className="landing-expert-headline" color="gray.900" mb={4}>
                Expert-led, horse-first
              </Heading>
              <Text className="landing-expert-body" color="gray.700" mb={5}>
                Our team combines deep expertise in equine medicine with modern trial management to deliver results that matter for horses and the people who care for them.
              </Text>
              <ul className="landing-expert-list">
                <li>
                  <Icon name="check_circle" />
                  Board-certified equine specialists
                </li>
                <li>
                  <Icon name="check_circle" />
                  FDA and regulatory experience
                </li>
                <li>
                  <Icon name="check_circle" />
                  Scalable biologics platform
                </li>
              </ul>
              <Link to="/about/team" className="landing-expert-link">
                Meet the team
                <Icon name="arrow_forward" aria-hidden="true" />
              </Link>
            </MotionBox>
          </div>
        </section>

        {/* Research grid — modern bento-style cards with icons and descriptions */}
        <section id="research" className="landing-research" aria-labelledby="research-heading">
          <div className="landing-research-header">
            <p className="landing-research-eyebrow">Research & evidence</p>
            <Heading as="h2" id="research-heading" size="2xl" textAlign="center" color="gray.900" mb={2}>
              Built on rigorous science. Proven in real horses.
            </Heading>
            <Text textAlign="center" color="gray.600" maxW="640px" mx="auto">
              Our products are backed by robust research and development, meticulous manufacturing, comprehensive clinical trials, and real-world use in leading equine referral centers.
            </Text>
          </div>
          <div className="landing-research-grid">
            <MotionBox
              className="landing-research-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-24px' }}
              transition={{ duration: 0.4 }}
            >
              <div className="landing-research-card-icon" aria-hidden="true">
                <Icon name="biotech" />
              </div>
              <h3 className="landing-research-card-title">Robust Science</h3>
              <p className="landing-research-card-desc">
                Developed by experts in peptide science, microbiology, and regenerative medicine to address unmet needs.
              </p>
            </MotionBox>
            <MotionBox
              className="landing-research-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-24px' }}
              transition={{ duration: 0.4, delay: 0.05 }}
            >
              <div className="landing-research-card-icon" aria-hidden="true">
                <Icon name="bloodtype" />
              </div>
              <h3 className="landing-research-card-title">Reliable Production</h3>
              <p className="landing-research-card-desc">
                Our sterile, cGMP manufacturing delivers high-quality products with reliable lot-to-lot performance.
              </p>
            </MotionBox>
            <MotionBox
              className="landing-research-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-24px' }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="landing-research-card-icon" aria-hidden="true">
                <Icon name="analytics" />
              </div>
              <h3 className="landing-research-card-title">Clinical Trial Results</h3>
              <p className="landing-research-card-desc">
                Every product in our pipeline is proven through extensive clinical trials led by specialists in their field.
              </p>
            </MotionBox>
            <MotionBox
              className="landing-research-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-24px' }}
              transition={{ duration: 0.4, delay: 0.15 }}
            >
              <div className="landing-research-card-icon" aria-hidden="true">
                <Icon name="handshake" />
              </div>
              <h3 className="landing-research-card-title">Real-World Outcomes</h3>
              <p className="landing-research-card-desc">
                We work with leading referral centers to integrate our products into their preferred treatment protocols.
              </p>
            </MotionBox>
          </div>
        </section>

        {/* Latest news — hidden until at least one post is published. */}
        <LatestNews />

        {/* Contact / Request demo — cinematic editorial CTA */}
        <section id="contact" className="landing-contact" aria-labelledby="contact-heading">
          <div className="landing-contact-visual" style={{ backgroundImage: `url(${ctaImageUrl})` }} aria-hidden="true" />
          <div className="landing-contact-inner">
            <MotionBox
              className="landing-contact-message"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.55 }}
            >
              <p className="landing-contact-eyebrow">Contact us</p>
              <Heading
                as="h2"
                id="contact-heading"
                className="landing-contact-heading"
                color="white"
                mb={5}
              >
                Get in touch with us about our products
              </Heading>
              <Text className="landing-contact-subcopy" color="whiteAlpha.900" maxW="480px" mb={5}>
                Talk to our team about clinical needs, partner studies, or use of our products.
              </Text>
              <ul className="landing-contact-details" aria-label="Direct contact">
                <li>
                  <Icon name="mail_outline" />
                  <Link to="/contact">Send us a message</Link>
                </li>
                <li>
                  <Icon name="call" />
                  <a href={PHONE_HREF}>{PHONE_DISPLAY}</a>
                </li>
                <li>
                  <Icon name="place" />
                  <span>{ADDRESS_LINE2}</span>
                </li>
              </ul>
            </MotionBox>
            <MotionBox
              className="landing-contact-form-wrap"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.55, delay: 0.1 }}
            >
              <div className="landing-contact-form-card">
                <div className="landing-contact-form-accent" aria-hidden="true" />
                <form onSubmit={handleContactSubmit}>
                  <VStack align="stretch" spacing={4}>
                    <p className="landing-contact-form-label">Contact us</p>
                    <Input
                      placeholder="Your email"
                      aria-label="Your email address"
                      autoComplete="email"
                      type="email"
                      bg="var(--color-gray-50)"
                      border="1px solid var(--color-gray-200)"
                      color="gray.800"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      _placeholder={{ color: 'gray.500' }}
                      _focus={{ borderColor: 'var(--color-primary-maroon)', boxShadow: '0 0 0 1px var(--color-primary-maroon)' }}
                      borderRadius="var(--radius-md)"
                      size="lg"
                    />
                    <Turnstile
                      ref={turnstileRef}
                      onVerify={setCaptchaToken}
                      onExpire={() => setCaptchaToken('')}
                      onError={() => setCaptchaToken('')}
                    />
                    <Button
                      type="submit"
                      size="lg"
                      className="landing-contact-submit"
                      color="white"
                      borderRadius="var(--radius-md)"
                      fontWeight={600}
                      isLoading={contactSubmitting}
                      isDisabled={!captchaToken}
                    >
                      Contact us
                    </Button>
                    {contactError && (
                      <Text role="alert" fontSize="sm" color="#B42318">
                        {contactError}
                      </Text>
                    )}
                    <Text fontSize="xs" color="gray.500" textAlign={{ base: 'center', md: 'left' }}>We'll respond within 24 hours.</Text>
                  </VStack>
                </form>
              </div>
            </MotionBox>
          </div>
        </section>

        {/* Footer */}
      </main>

      <LandingFooter
          brandCta={{ label: 'Learn about BIO-PLY™', to: '/products/bio-ply' }}
          columns={[
            {
              heading: 'Product',
              links: [
                { label: 'All products', href: '/products' },
                { label: 'BIO-PLY™', href: '/products/bio-ply' },
                { label: 'Features', href: '#features' },
              ],
            },
            {
              heading: 'Company',
              links: [
                { label: 'About', href: '#about' },
                { label: 'Our Team', href: '/about/team' },
                { label: 'Research', href: '#research' },
                { label: 'News', href: '/news', newsOnly: true },
              ],
            },
            {
              heading: 'Get started',
              links: [
                { label: 'Request access', href: '#request-access' },
                { label: 'Contact', href: '/contact' },
                { label: 'Login', href: '/login' },
              ],
            },
          ]}
        />
    </div>
  );
}
